/**
 * TransportPartners — Logistics Control Tower
 * Full CRUD: Create, Read, Update, Delete, Multi-select, Bulk delete
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../../components/ui/resizable';
import {
  Search, RefreshCw, Truck, Users, Phone, Mail, MapPin,
  X, Edit, Save, User, Package, Globe, Shield, Loader2,
  Building2, UserCheck, Hash, Plus, Trash2, AlertTriangle,
  ChevronRight, ChevronLeft, Check, Upload, Download,
  SlidersHorizontal, Weight, FilterX, Link2, Pencil
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { VEHICLE_TYPES, VEHICLE_CATEGORIES, getVehicleTypeLabel, getVehicleTypeIcon, getVehicleTypesByCategory } from '../../constants/vehicleTypes';
import VehicleTypeSelector from '../../components/VehicleTypeSelector';
import CSVImportModal from '../../components/CSVImportModal';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useAuth } from '../../contexts/AuthContext';
import { PERMS } from '../../utils/permissions';
import PartnerPerformance from '../../components/admin/PartnerPerformance';
import ExportFormatMenu from '../../components/common/ExportFormatMenu';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => 'cookie-auth';
const mono = { fontFamily: "'JetBrains Mono', monospace" };
const heading = { fontFamily: "'Manrope', sans-serif" };
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

const AVAIL = {
  disponible: { label: 'Disponible', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  indisponible: { label: 'Indisponible', cls: 'bg-red-50 text-red-600 border-red-200' },
  en_attente: { label: 'En attente', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  inconnu: { label: 'Non vérifié', cls: 'bg-zinc-50 text-zinc-500 border-zinc-200' }
};
const TYPE_LABELS = {
  'Entreprise ou societe de transport': 'Entreprise',
  'Personne physique': 'Particulier',
  'Personne morale': 'Société'
};
const TYPE_OPTIONS = [
  { value: 'Entreprise ou societe de transport', label: 'Entreprise' },
  { value: 'Personne physique', label: 'Particulier' },
  { value: 'Personne morale', label: 'Société / Personne morale' }
];
const VT_COLORS = {
  benne: '#f97316', plateau: '#3b82f6', citerne: '#06b6d4', semi_remorque: '#8b5cf6',
  porte_conteneur: '#ec4899', fourgon: '#64748b', frigorifique: '#0ea5e9', grue: '#eab308',
  carrosserie: '#a8a29e', autochargeuse: '#22c55e', porte_char: '#f43f5e', autre: '#9ca3af'
};
const VT_BG = {
  benne: 'bg-orange-500', plateau: 'bg-blue-500', citerne: 'bg-cyan-500', semi_remorque: 'bg-violet-500',
  porte_conteneur: 'bg-pink-500', fourgon: 'bg-slate-500', frigorifique: 'bg-sky-500', grue: 'bg-yellow-500',
  carrosserie: 'bg-stone-400', autochargeuse: 'bg-green-500', porte_char: 'bg-rose-500', autre: 'bg-gray-400'
};
const VT_LABELS = {
  plateau: 'Plateau', benne: 'Benne', citerne: 'Citerne', semi_remorque: 'Semi-remorque',
  porte_conteneur: 'Porte-conteneur', fourgon: 'Fourgon', frigorifique: 'Frigorifique',
  grue: 'Grue', carrosserie: 'Carrosserie', autochargeuse: 'Autochargeuse',
  porte_char: 'Porte-char', autre: 'Autre'
};
// La flotte declaree utilise parfois le prefixe "camion_" (ex: camion_plateau_grue)
const normalizeVT = (t) => {
  const raw = String(t || '').replace(/^camion_/, '');
  if (VT_LABELS[raw]) return raw;
  const first = raw.split('_')[0];
  return VT_LABELS[first] ? first : 'autre';
};
const vtLabel = (t) => {
  const raw = String(t || '');
  if (!raw) return 'Autre';
  const full = getVehicleTypeLabel(raw);
  if (full && full !== raw) return full;
  return VT_LABELS[normalizeVT(raw)] || 'Autre';
};
const vtIcon = (t) => getVehicleTypeIcon(String(t || ''));
// ============================================================
// Fleet Cell
// ============================================================
const FleetCell = ({ fleet, totalTrucks }) => {
  const count = totalTrucks || (fleet ? fleet.reduce((s, f) => s + (f.quantity || 0), 0) : 0);
  if (!fleet || fleet.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-300" style={mono}>0</span>
        <span className="text-[10px] text-zinc-300">aucun declare</span>
      </div>
    );
  }

  const formatFleetItem = (f) => {
    const label = f.vehicle_type_label || f.vehicle_type;
    const qty = f.quantity;
    const tons = f.capacity_tons;
    if (qty && tons) return `${qty}x ${label} (${tons}T)`;
    if (qty) return `${qty}x ${label}`;
    if (tons) return `${label} ${tons}T`;
    return label;
  };

  return (
    <div className="flex items-center gap-2">
      {count > 0 && <span className="text-xs font-bold text-zinc-700 min-w-[18px]" style={mono}>{count}</span>}
      <div className="flex items-center gap-1 flex-wrap">
        {fleet.slice(0, 3).map((f, i) => (
          <span key={i} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm border border-zinc-200 bg-white"
            title={formatFleetItem(f)}>
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: VT_COLORS[f.vehicle_type] || '#9ca3af' }} />
            <span className="text-zinc-600 font-medium whitespace-nowrap">{formatFleetItem(f)}</span>
          </span>
        ))}
        {fleet.length > 3 && <span className="text-[10px] text-zinc-400">+{fleet.length - 3}</span>}
      </div>
    </div>
  );
};

// ============================================================
// Create/Edit Partner Wizard (4 steps)
// ============================================================
const WIZARD_STEPS = [
  { id: 1, label: 'Identite', icon: Building2 },
  { id: 2, label: 'Coordonnees', icon: MapPin },
  { id: 3, label: 'Flotte', icon: Truck },
  { id: 4, label: 'Resume', icon: Check }
];

const COVERAGE_OPTIONS = [
  "Côte d'Ivoire", "Mali", "Burkina Faso", "Guinée", "Ghana",
  "Sénégal", "Togo", "Bénin", "Niger", "Libéria", "Sierra Leone"
];

const PartnerFormDialog = ({ open, onClose, onSaved, editPartner }) => {
  const isEdit = !!editPartner;
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({});
  const [fleet, setFleet] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    if (editPartner) {
      setForm({
        name: editPartner.name || '', type: editPartner.type || 'Personne physique',
        contactName: editPartner.contactName || '', email: editPartner.email || '',
        phone: editPartner.phone || '', phone2: editPartner.phone2 || '',
        address: editPartner.address || '', country: editPartner.country || "Côte d'Ivoire",
        intraInterLand: editPartner.intraInterLand || 'Intraland',
        coverageZones: editPartner.coverageZones || [],
        availability: editPartner.availability || 'inconnu',
        operatorComment: editPartner.operatorComment || ''
      });
      setFleet(editPartner.fleet || []);
    } else {
      setForm({ name: '', type: 'Personne physique', contactName: '', email: '', phone: '', phone2: '', address: '', country: "Côte d'Ivoire", intraInterLand: 'Intraland', coverageZones: [], availability: 'inconnu', operatorComment: '' });
      setFleet([]);
    }
  }, [editPartner, open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleCoverage = (zone) => {
    setForm(f => {
      const zones = f.coverageZones || [];
      return { ...f, coverageZones: zones.includes(zone) ? zones.filter(z => z !== zone) : [...zones, zone] };
    });
  };

  const addFleetItem = (vehicleValue) => {
    const vt = VEHICLE_TYPES.find(t => t.value === vehicleValue);
    if (!vt) return;
    const existing = fleet.findIndex(f => f.vehicle_type === vehicleValue);
    if (existing >= 0) {
      setFleet(f => f.map((item, i) => i === existing ? { ...item, quantity: (item.quantity || 0) + 1 } : item));
    } else {
      setFleet(f => [...f, { vehicle_type: vehicleValue, vehicle_type_label: vt.label, quantity: 1, capacity_tons: null }]);
    }
  };
  const removeFleetItem = (i) => setFleet(f => f.filter((_, j) => j !== i));
  const updateFleetQty = (i, qty) => setFleet(f => f.map((item, j) => j === i ? { ...item, quantity: qty } : item));
  const updateFleetCap = (i, cap) => setFleet(f => f.map((item, j) => j === i ? { ...item, capacity_tons: cap } : item));

  const canNext = () => {
    if (step === 1) return form.name?.trim();
    return true;
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = { ...form, fleet };
      if (isEdit) {
        await axios.put(`${API}/api/transport-partners/${editPartner.id}`, payload, { headers: headers() });
        toast({ title: 'Partenaire mis a jour' });
      } else {
        await axios.post(`${API}/api/transport-partners`, payload, { headers: headers() });
        toast({ title: 'Partenaire créé' });
      }
      onSaved();
      onClose();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const groupedTypes = getVehicleTypesByCategory();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] p-0 overflow-hidden" data-testid="partner-form-dialog">
        {/* Stepper Header */}
        <div className="px-6 pt-5 pb-3 border-b">
          <h2 className="text-base font-semibold text-zinc-800 mb-3" style={heading}>
            {isEdit ? 'Modifier le partenaire' : 'Nouveau partenaire'}
          </h2>
          <div className="flex items-center gap-1">
            {WIZARD_STEPS.map((s, idx) => {
              const isActive = step === s.id;
              const isDone = step > s.id;
              return (
                <React.Fragment key={s.id}>
                  {idx > 0 && <div className={`flex-1 h-px mx-1 ${isDone ? 'bg-emerald-400' : 'bg-zinc-200'}`} />}
                  <button
                    onClick={() => { if (isDone || isActive) setStep(s.id); }}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[11px] font-medium transition-all ${
                      isActive ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                      isDone ? 'text-emerald-600 hover:bg-emerald-50/50' : 'text-zinc-400'
                    }`}
                    data-testid={`wizard-step-${s.id}`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${
                      isActive ? 'bg-emerald-500 text-white' : isDone ? 'bg-emerald-100 text-emerald-600' : 'bg-zinc-100 text-zinc-400'
                    }`}>
                      {isDone ? <Check className="h-3 w-3" /> : s.id}
                    </div>
                    <span className="hidden sm:inline">{s.label}</span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <ScrollArea className="max-h-[55vh]">
          <div className="px-6 py-4">
            {/* Step 1: Identite */}
            {step === 1 && (
              <div className="space-y-4" data-testid="wizard-step-1-content">
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1 block">Nom du transporteur *</label>
                  <Input value={form.name || ''} onChange={e => set('name', e.target.value)} placeholder="Ex: SOTHTRA, Ali Transport..." className="h-9 text-sm rounded-sm" data-testid="form-name" autoFocus />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1.5 block">Type de structure</label>
                  <div className="grid grid-cols-3 gap-2">
                    {TYPE_OPTIONS.map(t => (
                      <button key={t.value} onClick={() => set('type', t.value)} data-testid={`type-${t.value.substring(0, 10)}`}
                        className={`p-3 rounded-sm border text-left transition-all ${form.type === t.value ? 'border-emerald-400 bg-emerald-50/50 ring-1 ring-emerald-200' : 'border-zinc-200 hover:border-zinc-300'}`}>
                        <p className="text-sm font-medium text-zinc-800">{t.label}</p>
                        <p className="text-[10px] text-zinc-400 mt-0.5">{t.value === 'Entreprise ou societe de transport' ? 'Societe formelle' : t.value === 'Personne physique' ? 'Proprietaire individuel' : 'Entite juridique'}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1 block">Nom du contact principal</label>
                  <Input value={form.contactName || ''} onChange={e => set('contactName', e.target.value)} placeholder="Nom et prenom" className="h-9 text-sm rounded-sm" data-testid="form-contact" />
                </div>
              </div>
            )}

            {/* Step 2: Coordonnees */}
            {step === 2 && (
              <div className="space-y-4" data-testid="wizard-step-2-content">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1 block">Téléphone principal</label>
                    <Input value={form.phone || ''} onChange={e => set('phone', e.target.value)} placeholder="+225 XX XX XX XX" className="h-9 text-sm rounded-sm" data-testid="form-phone" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1 block">Téléphone secondaire</label>
                    <Input value={form.phone2 || ''} onChange={e => set('phone2', e.target.value)} className="h-9 text-sm rounded-sm" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1 block">Email</label>
                  <Input value={form.email || ''} onChange={e => set('email', e.target.value)} type="email" placeholder="contact@exemple.com" className="h-9 text-sm rounded-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1 block">Adresse</label>
                  <Input value={form.address || ''} onChange={e => set('address', e.target.value)} placeholder="Ville, quartier..." className="h-9 text-sm rounded-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1.5 block">Type de desserte</label>
                  <div className="flex gap-2">
                    {['Intraland', 'Hinterland'].map(v => (
                      <button key={v} onClick={() => set('intraInterLand', v)}
                        className={`flex-1 py-2 px-3 rounded-sm border text-sm font-medium transition-all ${form.intraInterLand === v ? 'border-emerald-400 bg-emerald-50/50 text-emerald-700' : 'border-zinc-200 text-zinc-500 hover:border-zinc-300'}`}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1.5 block">Zones couvertes</label>
                  <div className="flex flex-wrap gap-1.5">
                    {COVERAGE_OPTIONS.map(z => (
                      <button key={z} onClick={() => toggleCoverage(z)}
                        className={`px-2.5 py-1 rounded-sm border text-[11px] font-medium transition-all ${
                          (form.coverageZones || []).includes(z) ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-zinc-200 text-zinc-500 hover:border-zinc-300'
                        }`}>
                        {z}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Flotte */}
            {step === 3 && (
              <div className="space-y-4" data-testid="wizard-step-3-content">
                <p className="text-xs text-zinc-500">Sélectionnez les types de véhicules operes par ce transporteur.</p>

                {/* Category browser */}
                {Object.entries(groupedTypes).map(([catKey, catData]) => {
                  if (!catData.types || catData.types.length === 0) return null;
                  const hasSelected = catData.types.some(t => fleet.some(f => f.vehicle_type === t.value));
                  return (
                    <div key={catKey}>
                      <h4 className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1.5 flex items-center gap-1.5">
                        {catData.label.replace(/^[^\s]+\s/, '')}
                        {hasSelected && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                      </h4>
                      <div className="flex flex-wrap gap-1">
                        {catData.types.map(vt => {
                          const inFleet = fleet.find(f => f.vehicle_type === vt.value);
                          return (
                            <button key={vt.value} onClick={() => inFleet ? removeFleetItem(fleet.indexOf(inFleet)) : addFleetItem(vt.value)}
                              data-testid={`vt-${vt.value}`}
                              className={`px-2 py-1 rounded-sm border text-[11px] font-medium transition-all ${
                                inFleet ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-zinc-200 text-zinc-500 hover:border-zinc-300 hover:bg-zinc-50'
                              }`}>
                              {inFleet && <Check className="h-3 w-3 inline mr-0.5" />}{vt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* Selected fleet with quantities */}
                {fleet.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <h4 className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-2">Vehicules selectionnes ({fleet.length})</h4>
                    <div className="space-y-1.5">
                      {fleet.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 py-1.5 px-2.5 rounded-sm bg-emerald-50/50 border border-emerald-100">
                          <Check className="h-3 w-3 text-emerald-500 shrink-0" />
                          <span className="text-sm font-medium text-zinc-800 flex-1">{f.vehicle_type_label}</span>
                          <div className="flex items-center gap-1.5">
                            <label className="text-[9px] text-zinc-400">Qte</label>
                            <Input type="number" min="1" value={f.quantity || ''} onChange={e => updateFleetQty(i, parseInt(e.target.value) || 0)}
                              className="h-6 w-14 text-[11px] text-center rounded-sm px-1" style={mono} />
                            <label className="text-[9px] text-zinc-400 ml-1">Tonnes</label>
                            <Input type="number" min="0" value={f.capacity_tons || ''} onChange={e => updateFleetCap(i, parseInt(e.target.value) || null)}
                              className="h-6 w-14 text-[11px] text-center rounded-sm px-1" style={mono} placeholder="-" />
                            <Button variant="ghost" size="icon" className="h-5 w-5 text-red-400 hover:text-red-600" onClick={() => removeFleetItem(i)}><X className="h-3 w-3" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Resume */}
            {step === 4 && (
              <div className="space-y-4" data-testid="wizard-step-4-content">
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1 block">Disponibilite</label>
                  <div className="flex gap-2">
                    {Object.entries(AVAIL).map(([k, v]) => (
                      <button key={k} onClick={() => set('availability', k)}
                        className={`flex-1 py-2 px-3 rounded-sm border text-sm font-medium transition-all ${form.availability === k ? `${v.cls} ring-1` : 'border-zinc-200 text-zinc-500'}`}>
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1 block">Note operateur</label>
                  <Textarea value={form.operatorComment || ''} onChange={e => set('operatorComment', e.target.value)}
                    placeholder="Informations complementaires, conditions particulieres..." className="min-h-[60px] text-sm rounded-sm" />
                </div>
                <Separator />
                <div className="bg-zinc-50 rounded-sm border p-4 space-y-3">
                  <h4 className="text-xs font-bold text-zinc-700" style={heading}>Recapitulatif</h4>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                    <div className="text-zinc-400 text-[11px]">Nom</div><div className="font-medium text-zinc-800">{form.name || '-'}</div>
                    <div className="text-zinc-400 text-[11px]">Type</div><div className="text-zinc-700">{TYPE_LABELS[form.type] || form.type}</div>
                    <div className="text-zinc-400 text-[11px]">Contact</div><div className="text-zinc-700">{form.contactName || '-'}</div>
                    <div className="text-zinc-400 text-[11px]">Téléphone</div><div className="text-zinc-700" style={mono}>{form.phone || '-'}</div>
                    <div className="text-zinc-400 text-[11px]">Email</div><div className="text-zinc-700">{form.email || '-'}</div>
                    <div className="text-zinc-400 text-[11px]">Adresse</div><div className="text-zinc-700">{form.address || '-'}</div>
                    <div className="text-zinc-400 text-[11px]">Desserte</div><div className="text-zinc-700">{form.intraInterLand}</div>
                    <div className="text-zinc-400 text-[11px]">Zones</div><div className="text-zinc-700">{(form.coverageZones || []).join(', ') || '-'}</div>
                  </div>
                  {fleet.length > 0 && (
                    <div className="pt-2 border-t mt-2">
                      <p className="text-[11px] text-zinc-400 mb-1">Flotte ({fleet.reduce((s, f) => s + (f.quantity || 0), 0)} vehicules)</p>
                      <div className="flex flex-wrap gap-1">
                        {fleet.map((f, i) => (
                          <Badge key={i} variant="outline" className="text-[10px] rounded-sm h-5 gap-1">
                            {f.quantity}x {f.vehicle_type_label}{f.capacity_tons ? ` (${f.capacity_tons}T)` : ''}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer Navigation */}
        <div className="px-6 py-3 border-t bg-zinc-50/50 flex items-center justify-between">
          <div className="text-[10px] text-zinc-400" style={mono}>Etape {step}/4</div>
          <div className="flex items-center gap-2">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(s => s - 1)} className="text-xs h-8 rounded-sm gap-1" data-testid="wizard-prev">
                <ChevronLeft className="h-3 w-3" />Precedent
              </Button>
            )}
            {step < 4 ? (
              <Button onClick={() => setStep(s => s + 1)} disabled={!canNext()} className="bg-emerald-600 hover:bg-emerald-700 text-xs h-8 rounded-sm gap-1" data-testid="wizard-next">
                Suivant<ChevronRight className="h-3 w-3" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-xs h-8 rounded-sm gap-1" data-testid="form-submit">
                {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3 w-3 mr-1" />}
                {isEdit ? 'Sauvegarder' : 'Creer le partenaire'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// Delete Confirmation Dialog
// ============================================================
const DeleteDialog = ({ open, onClose, onConfirm, count }) => (
  <AlertDialog open={open} onOpenChange={onClose}>
    <AlertDialogContent data-testid="delete-confirm-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-red-500" />Confirmer la suppression</AlertDialogTitle>
        <AlertDialogDescription>
          {count > 1
            ? `Vous allez supprimer ${count} partenaires. Les chauffeurs lies seront automatiquement delies.`
            : 'Vous allez supprimer ce partenaire. Les chauffeurs lies seront automatiquement delies.'}
          <br />Cette action est irreversible.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="text-xs h-8 rounded-sm">Annuler</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-700 text-xs h-8 rounded-sm" data-testid="confirm-delete-btn">
          Supprimer{count > 1 ? ` (${count})` : ''}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// ============================================================
// Detail Panel (read + edit tabs)
// ============================================================
const DetailPanel = ({ item, type, onClose, onRefresh, onEdit }) => {
  const [tab, setTab] = useState('info');
  if (!item) return null;
  const isPartner = type === 'partner';
  const fleet = item.fleet || [];
  const avail = AVAIL[item.availability || item.status] || AVAIL.inconnu;

  return (
    <div className="h-full flex flex-col bg-white" data-testid="detail-panel">
      <div className="px-5 py-4 border-b flex-shrink-0">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-md flex items-center justify-center text-white text-sm font-bold ${isPartner ? 'bg-emerald-600' : 'bg-amber-500'}`}>
              {(item.name || item.firstName || '?').substring(0, 2).toUpperCase()}
            </div>
            <div>
              <h2 className="text-base font-semibold text-zinc-900" style={heading}>{isPartner ? item.name : `${item.firstName} ${item.lastName}`}</h2>
              <div className="flex items-center gap-1.5 mt-0.5">
                <Badge className={`text-[10px] h-[18px] px-1.5 border font-medium ${avail.cls}`}>{avail.label}</Badge>
                {isPartner && <span className="text-[11px] text-zinc-400">{TYPE_LABELS[item.type] || item.type}</span>}
                {!isPartner && <Badge className="text-[10px] h-[18px] px-1.5 border font-medium bg-amber-50 text-amber-700 border-amber-200">Independant</Badge>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {isPartner && (
              <Button variant="outline" size="sm" className="h-7 text-[11px] rounded-sm" onClick={() => onEdit(item)} data-testid="edit-btn">
                <Edit className="h-3 w-3 mr-1" />Modifier
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400" onClick={onClose} data-testid="close-detail-btn"><X className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-3 text-[11px]">
          {[
            { label: 'Camions', value: isPartner ? (item.totalDeclaredTrucks || 0) : (item.totalTrucks || 0), color: 'text-zinc-800' },
            { label: 'Chauffeurs', value: isPartner ? (item.linked_drivers || []).length : '-', color: 'text-blue-600' },
            { label: 'Operations', value: isPartner ? (item.operationsCount || 0) : '-', color: 'text-emerald-600' },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <span className="text-zinc-400 uppercase tracking-wider font-medium" style={{ fontSize: 9 }}>{s.label}</span>
              <span className={`font-bold ${s.color}`} style={mono}>{s.value}</span>
            </div>
          ))}
        </div>
      </div>

      {isPartner ? (
        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="w-full justify-start px-5 bg-transparent border-b h-9 rounded-none gap-0">
            {[
              { v: 'info', icon: Package, l: 'Details' },
              { v: 'fleet', icon: Truck, l: 'Flotte' },
              { v: 'vehicles', icon: Truck, l: `Vehicules (${(item.linked_vehicles || []).length})` },
              { v: 'drivers', icon: Users, l: `Chauffeurs (${(item.linked_drivers || []).length})` },
              { v: 'performance', icon: SlidersHorizontal, l: 'Performance' },
            ].map(t => (
              <TabsTrigger key={t.v} value={t.v} className="text-[11px] rounded-none h-9 border-b-2 border-transparent data-[state=active]:border-emerald-500 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none px-3 gap-1"><t.icon className="h-3 w-3" />{t.l}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="info" className="flex-1 overflow-auto m-0"><ScrollArea className="h-full"><div className="p-5 space-y-4"><InfoContent item={item} /></div></ScrollArea></TabsContent>
          <TabsContent value="fleet" className="flex-1 overflow-auto m-0"><ScrollArea className="h-full"><div className="p-5"><FleetContent fleet={fleet} totalTrucks={item.totalDeclaredTrucks} linkedVehicles={item.linked_vehicles} /></div></ScrollArea></TabsContent>
          <TabsContent value="vehicles" className="flex-1 overflow-auto m-0"><ScrollArea className="h-full"><div className="p-5"><RealVehiclesContent partnerId={item.id} vehicles={item.linked_vehicles || []} fleet={fleet} onRefresh={onRefresh} /></div></ScrollArea></TabsContent>
          <TabsContent value="drivers" className="flex-1 overflow-auto m-0"><ScrollArea className="h-full"><div className="p-5"><RealDriversContent partnerId={item.id} drivers={item.linked_drivers || []} onRefresh={onRefresh} /></div></ScrollArea></TabsContent>
          <TabsContent value="performance" className="flex-1 overflow-auto m-0"><ScrollArea className="h-full"><div className="p-5"><PartnerPerformance partnerId={item.id} /></div></ScrollArea></TabsContent>
        </Tabs>
      ) : (
        <ScrollArea className="flex-1"><div className="p-5 space-y-4"><DriverInfoContent driver={item} /></div></ScrollArea>
      )}
    </div>
  );
};

// --- Sub-components ---
const Section = ({ title, children }) => (<div><h4 className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-2">{title}</h4><div className="space-y-1">{children}</div></div>);
const EmptyBlock = ({ icon: Icon, text }) => (<div className="text-center py-8 text-zinc-400"><Icon className="h-6 w-6 mx-auto mb-2 opacity-30" /><p className="text-xs">{text}</p></div>);

const InfoContent = ({ item }) => (
  <>
    <Section title="Contact">
      {[{ icon: User, val: item.contactName }, { icon: Phone, val: item.phone }, { icon: Phone, val: item.phone2, dim: true }, { icon: Mail, val: item.email }, { icon: MapPin, val: item.address }].filter(c => c.val).map((c, i) => (
        <p key={i} className={`text-sm flex items-center gap-2 ${c.dim ? 'text-zinc-400' : 'text-zinc-700'}`}><c.icon className="h-3.5 w-3.5 text-zinc-300 shrink-0" />{c.val}</p>
      ))}
    </Section>
    <Section title="Couverture">
      <div className="flex flex-wrap gap-1">
        {item.intraInterLand && <Badge variant="outline" className="text-[10px] rounded-sm h-5">{item.intraInterLand}</Badge>}
        {item.country && <Badge variant="outline" className="text-[10px] rounded-sm h-5"><Globe className="h-2.5 w-2.5 mr-0.5" />{item.country}</Badge>}
        {(item.coverageZones || []).map((z, i) => <Badge key={i} variant="outline" className="text-[10px] rounded-sm h-5 bg-blue-50/50">{z}</Badge>)}
      </div>
    </Section>
    {item.operatorComment && (
      <Section title="Note operateur">
        <p className="text-xs text-zinc-600 bg-zinc-50 p-3 rounded-sm border whitespace-pre-wrap leading-relaxed">{item.operatorComment}</p>
      </Section>
    )}
    <div className="text-[9px] text-zinc-300 pt-2 space-y-0.5" style={mono}>
      <p>ID: {item.truckDbMatricule || item.id?.substring(0, 8)}</p>
      <p>Source: {item.importedFrom || 'N/A'} / {item.importedAt ? new Date(item.importedAt).toLocaleString('fr-FR') : 'N/A'}</p>
    </div>
  </>
);

const FleetContent = ({ fleet, totalTrucks, linkedVehicles }) => (
  <>
    {(!fleet || fleet.length === 0) ? <EmptyBlock icon={Truck} text="Aucune flotte declaree" /> : (
      <div className="space-y-1.5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">{fleet.length} type{fleet.length > 1 ? 's' : ''}</span>
          <span className="text-xs text-zinc-500" style={mono}>{totalTrucks || '?'} total</span>
        </div>
        {fleet.map((f, i) => (
          <div key={i} className="flex items-center gap-3 py-2 px-3 rounded-sm border border-zinc-100 hover:border-zinc-200 transition-colors">
            <div className={`w-2 h-8 rounded-full ${VT_BG[f.vehicle_type] || 'bg-gray-400'}`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-zinc-800">{f.vehicle_type_label}</p>
              <p className="text-[10px] text-zinc-400" style={mono}>{f.quantity != null && `${f.quantity} vehicule${f.quantity > 1 ? 's' : ''}`}{f.capacity_tons && ` / ${f.capacity_tons}T`}</p>
            </div>
          </div>
        ))}
      </div>
    )}
    {(linkedVehicles || []).length > 0 && (
      <div className="mt-5">
        <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 block mb-2">Véhicules immatricules</span>
        {linkedVehicles.map(v => (<div key={v.id} className="flex items-center gap-2 py-1.5 px-2 border-b border-zinc-50 text-sm"><Hash className="h-3 w-3 text-zinc-300" /><span className="font-medium text-zinc-800" style={mono}>{v.plateNumber}</span><span className="text-zinc-400 text-xs">{v.vehicleType}</span></div>))}
      </div>
    )}
  </>
);

const DriversContent = ({ drivers }) => (
  (!drivers || drivers.length === 0) ? <EmptyBlock icon={Users} text="Aucun chauffeur lie" /> : (
    <div className="space-y-1">
      <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 block mb-2">{drivers.length} chauffeur{drivers.length > 1 ? 's' : ''}</span>
      {drivers.map(d => (
        <div key={d.id} className="flex items-center gap-2.5 py-2 px-2 rounded-sm hover:bg-zinc-50 transition-colors">
          <div className="w-7 h-7 rounded-full bg-zinc-200 flex items-center justify-center text-[9px] font-bold text-zinc-500">{(d.firstName || '?')[0]}{(d.lastName || '?')[0]}</div>
          <div className="flex-1 min-w-0"><p className="text-sm font-medium text-zinc-800 truncate">{d.firstName} {d.lastName}</p><p className="text-[10px] text-zinc-400">{d.phone}</p></div>
          <Badge className={`text-[9px] h-4 px-1.5 border ${d.status === 'disponible' ? AVAIL.disponible.cls : AVAIL.inconnu.cls}`}>{d.status}</Badge>
        </div>
      ))}
    </div>
  )
);

const DriverInfoContent = ({ driver }) => {
  const fleet = driver.fleet || [];
  return (
    <>
      <Section title="Contact">
        {driver.phone && <p className="text-sm text-zinc-700 flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-zinc-300" />{driver.phone}</p>}
        {driver.country && <p className="text-sm text-zinc-700 flex items-center gap-2"><Globe className="h-3.5 w-3.5 text-zinc-300" />{driver.country}</p>}
      </Section>
      {(driver.paysDesservis || []).length > 0 && (
        <Section title="Couverture">
          <div className="flex flex-wrap gap-1">
            {driver.intraInterLand && <Badge variant="outline" className="text-[10px] rounded-sm h-5">{driver.intraInterLand}</Badge>}
            {driver.paysDesservis.map((p, i) => <Badge key={i} variant="outline" className="text-[10px] rounded-sm h-5 bg-blue-50/50">{p}</Badge>)}
          </div>
        </Section>
      )}
      <Section title={`Flotte declaree (${fleet.length} type${fleet.length > 1 ? 's' : ''})`}>
        {fleet.map((f, i) => (
          <div key={i} className="flex items-center gap-3 py-2 px-3 rounded-sm border border-zinc-100">
            <div className={`w-2 h-8 rounded-full ${VT_BG[f.vehicle_type] || 'bg-gray-400'}`} />
            <div className="flex-1"><p className="text-sm font-medium text-zinc-800">{f.vehicle_type_label}</p><p className="text-[10px] text-zinc-400" style={mono}>{f.quantity != null && `${f.quantity} vehicule${f.quantity > 1 ? 's' : ''}`}{f.capacity_tons && ` / ${f.capacity_tons}T`}</p></div>
          </div>
        ))}
      </Section>
    </>
  );
};

// --- Real Vehicles Content (CRUD) ---
const RealVehiclesContent = ({ partnerId, vehicles, fleet, onRefresh }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [newPlate, setNewPlate] = useState('');
  const [newType, setNewType] = useState('');
  const [newBrand, setNewBrand] = useState('');
  const [newModel, setNewModel] = useState('');
  const [newCapacity, setNewCapacity] = useState('');
  const [newHasTrailer, setNewHasTrailer] = useState(false);
  const [newTrailerPlate, setNewTrailerPlate] = useState('');
  const [plateError, setPlateError] = useState('');
  const [quickAdded, setQuickAdded] = useState([]);
  const [adding, setAdding] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState(new Set());
  const [batchForms, setBatchForms] = useState([]);

  // Link existing vehicle state
  const [showLink, setShowLink] = useState(false);
  const [linkSearch, setLinkSearch] = useState('');
  const [linkResults, setLinkResults] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);
  const linkDebounceRef = React.useRef(null);

  const searchExistingVehicles = React.useCallback(async (q) => {
    setLinkLoading(true);
    try {
      const params = new URLSearchParams({ exclude_partner_id: partnerId, limit: '30' });
      if (q && q.length >= 1) params.append('search', q);
      const res = await axios.get(`${API}/api/vehicles/search-for-linking?${params}`, {});
      setLinkResults(res.data.vehicles || []);
    } catch { setLinkResults([]); }
    finally { setLinkLoading(false); }
  }, [partnerId]);

  const onLinkSearch = (q) => {
    setLinkSearch(q);
    clearTimeout(linkDebounceRef.current);
    linkDebounceRef.current = setTimeout(() => searchExistingVehicles(q), 300);
  };

  const handleLinkVehicle = async (vehicleId) => {
    try {
      await axios.post(`${API}/api/transport-partners/${partnerId}/link-vehicle`, {
        vehicle_id: vehicleId
      }, {});
      toast({ title: 'Véhicule lié avec succès' });
      setShowLink(false);
      setLinkSearch('');
      setLinkResults([]);
      onRefresh();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    }
  };

  const fleetItems = fleet || [];

  const toggleTemplate = (idx) => {
    setSelectedTemplates(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const selectAllTemplates = () => {
    if (selectedTemplates.size === fleetItems.length) {
      setSelectedTemplates(new Set());
    } else {
      setSelectedTemplates(new Set(fleetItems.map((_, i) => i)));
    }
  };

  const startBatchAdd = () => {
    const forms = [...selectedTemplates].sort().map(idx => {
      const f = fleetItems[idx];
      return {
        idx,
        type: normalizeVT(f.vehicle_type),
        typeLabel: f.vehicle_type_label || vtLabel(f.vehicle_type),
        capacity: f.capacity_tons ? String(f.capacity_tons) : '',
        plate: '',
        brand: '',
        model: '',
      };
    });
    setBatchForms(forms);
    setShowAdd(false);
  };

  const updateBatchForm = (i, field, value) => {
    setBatchForms(prev => prev.map((f, idx) => idx === i ? { ...f, [field]: value } : f));
  };

  const removeBatchForm = (i) => {
    setBatchForms(prev => prev.filter((_, idx) => idx !== i));
  };

  const submitBatch = async () => {
    const validForms = batchForms.filter(f => f.plate.trim());
    if (validForms.length === 0) return;
    setAdding(true);
    try {
      for (const f of validForms) {
        await axios.post(`${API}/api/transport-partners/${partnerId}/vehicles`, {
          plate: f.plate.trim(),
          vehicleType: f.type,
          vehicleTypeLabel: f.typeLabel || vtLabel(f.type),
          brand: f.brand,
          model: f.model || '',
          capacityTons: parseFloat(f.capacity) || 0,
        }, { headers: { 'Authorization': 'Bearer cookie-auth' } });
      }
      setBatchForms([]);
      setSelectedTemplates(new Set());
      onRefresh();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setAdding(false); }
  };

  const prefillFromFleet = (fleetItem) => {
    if (fleetItem) {
      setNewType(normalizeVT(fleetItem.vehicle_type));
      setNewCapacity(fleetItem.capacity_tons ? String(fleetItem.capacity_tons) : '');
    }
    setBatchForms([]);
    setSelectedTemplates(new Set());
    setShowAdd(true);
  };

  const addVehicle = async () => {
    const plate = newPlate.trim().toUpperCase();
    if (!plate) {
      setPlateError("Saisissez la plaque d'immatriculation");
      return;
    }
    setPlateError('');
    setAdding(true);
    try {
      await axios.post(`${API}/api/transport-partners/${partnerId}/vehicles`, {
        plate,
        vehicleType: newType || 'autre',
        vehicleTypeLabel: vtLabel(newType || 'autre'),
        brand: newBrand.trim(),
        model: newModel.trim(),
        capacityTons: parseFloat(newCapacity) || 0,
        hasTrailer: newHasTrailer,
        trailerPlate: newHasTrailer ? newTrailerPlate.trim().toUpperCase() : '',
      }, { headers: { 'Authorization': 'Bearer cookie-auth' } });
      // Quick add : on garde le formulaire ouvert pour enchaîner les plaques
      setQuickAdded(prev => [...prev, plate]);
      setNewPlate(''); setNewBrand(''); setNewModel(''); setNewTrailerPlate('');
      toast({ title: `${plate} ajouté à la flotte`, description: 'Complétez les détails plus tard si besoin.' });
      onRefresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setPlateError(typeof detail === 'string' ? detail : (detail?.message || 'Erreur lors de l\'ajout'));
    } finally { setAdding(false); }
  };

  const removeVehicle = async (vehicleId) => {
    try {
      await axios.delete(`${API}/api/transport-partners/${partnerId}/vehicles/${vehicleId}`, { headers: { 'Authorization': 'Bearer cookie-auth' } });
      onRefresh();
    } catch (e) {
      toast({ title: 'Erreur de suppression', variant: 'destructive' });
    }
  };

  const getPlate = (v) => v.plate || v.registration || v.plateNumber || '?';
  const getCapacity = (v) => v.capacityTons || v.capacity || null;
  const allSelected = selectedTemplates.size === fleetItems.length && fleetItems.length > 0;

  return (
    <div className="space-y-3" data-testid="real-vehicles-content">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">Vehicules identifies ({vehicles.length})</span>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" className="h-7 text-[11px] text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => { setShowLink(!showLink); setShowAdd(false); setBatchForms([]); if (!showLink) searchExistingVehicles(''); }} data-testid="link-existing-vehicle-btn">
            <Link2 className="h-3 w-3 mr-1" />Lier existant
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => { setShowAdd(!showAdd); setShowLink(false); setBatchForms([]); setSelectedTemplates(new Set()); }} data-testid="add-vehicle-btn">
            <Plus className="h-3 w-3 mr-1" />Ajouter
          </Button>
        </div>
      </div>

      {/* Link existing vehicle panel */}
      {showLink && (
        <div className="p-3 border border-emerald-200 rounded-lg bg-emerald-50/30 space-y-2" data-testid="link-vehicle-panel">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700">Rechercher un véhicule existant</p>
            <span className="text-[10px] text-zinc-400">{linkResults.length} resultat{linkResults.length > 1 ? 's' : ''}</span>
          </div>
          <Input
            placeholder="Plaque, marque, modele, transporteur..."
            value={linkSearch}
            onChange={(e) => onLinkSearch(e.target.value)}
            className="h-8 text-sm"
            autoFocus
            data-testid="link-vehicle-search"
          />
          <div className="max-h-[280px] overflow-y-auto space-y-1">
            {linkLoading ? (
              <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-emerald-500" /></div>
            ) : linkResults.length === 0 ? (
              <p className="text-xs text-zinc-400 text-center py-4">Aucun véhicule disponible</p>
            ) : (
              linkResults.map(v => (
                <button
                  key={v.id}
                  onClick={() => handleLinkVehicle(v.id)}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg border border-transparent hover:bg-white hover:border-emerald-200 transition-colors text-left"
                  data-testid={`link-vehicle-option-${v.id}`}
                >
                  <div className={`w-7 h-7 rounded-md flex items-center justify-center text-white text-[9px] font-bold shrink-0 ${VT_BG[normalizeVT(v.vehicleType)] || 'bg-gray-400'}`}>
                    {vtLabel(v.vehicleType).substring(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-zinc-800" style={mono}>{v.registration || v.plateNumber || '?'}</p>
                    <p className="text-[10px] text-zinc-400">
                      {vtLabel(v.vehicleType)}
                      {v.brand ? ` · ${v.brand}` : ''}
                      {v.model ? ` ${v.model}` : ''}
                      {v.capacity ? ` · ${v.capacity}T` : ''}
                      {v.cuKg ? ` · CU: ${v.cuKg}Kg` : ''}
                    </p>
                  </div>
                  {v.partnerName ? (
                    <Badge className="text-[9px] h-4 px-1.5 bg-amber-50 text-amber-600 border-amber-200 rounded-full shrink-0">{v.partnerName}</Badge>
                  ) : (
                    <Badge className="text-[9px] h-4 px-1.5 bg-zinc-50 text-zinc-400 border-zinc-200 rounded-full shrink-0">Non lie</Badge>
                  )}
                </button>
              ))
            )}
          </div>
          <Button size="sm" variant="ghost" className="h-6 text-[10px] text-zinc-400" onClick={() => { setShowLink(false); setLinkSearch(''); }}>Fermer</Button>
        </div>
      )}

      {/* Fleet templates with selection */}
      {fleetItems.length > 0 && !showAdd && batchForms.length === 0 && (
        <div className="p-3 border border-dashed border-zinc-200 rounded-lg bg-zinc-50/50" data-testid="fleet-templates">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400">Flotte declaree</p>
            <div className="flex items-center gap-2">
              {selectedTemplates.size > 0 && (
                <Button size="sm" className="h-6 text-[10px] bg-emerald-600 hover:bg-emerald-700 rounded-md px-2.5 gap-1" onClick={startBatchAdd} data-testid="enrich-selected-btn">
                  <Plus className="h-3 w-3" />Enrichir {selectedTemplates.size === fleetItems.length ? 'tous' : `(${selectedTemplates.size})`}
                </Button>
              )}
              <button onClick={selectAllTemplates}
                className={`text-[10px] font-medium px-2 py-0.5 rounded-md border transition-colors ${allSelected ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-zinc-200 text-zinc-500 hover:border-emerald-200 hover:text-emerald-600'}`}
                data-testid="select-all-templates">
                {allSelected ? 'Tout deselectionner' : 'Tout selectionner'}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {fleetItems.map((f, i) => {
              const label = f.vehicle_type_label || vtLabel(f.vehicle_type);
              const tons = f.capacity_tons;
              const qty = f.quantity;
              const isSelected = selectedTemplates.has(i);
              return (
                <button key={i} onClick={() => toggleTemplate(i)}
                  className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg border transition-all cursor-pointer ${isSelected ? 'border-emerald-400 bg-emerald-50 ring-1 ring-emerald-200' : 'border-zinc-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/50'}`}
                  data-testid={`fleet-template-${i}`}>
                  <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-zinc-300 bg-white'}`}>
                    {isSelected && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: VT_COLORS[f.vehicle_type] || '#9ca3af' }} />
                  <span className={`font-medium ${isSelected ? 'text-emerald-700' : 'text-zinc-700'}`}>
                    {qty ? `${qty}x ` : ''}{label}{tons ? ` ${tons}T` : ''}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Batch add forms */}
      {batchForms.length > 0 && (
        <div className="space-y-2" data-testid="batch-add-forms">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">{batchForms.length} vehicule{batchForms.length > 1 ? 's' : ''} a enrichir</p>
            <div className="flex items-center gap-1.5">
              <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 rounded-lg gap-1" onClick={submitBatch} disabled={adding || batchForms.every(f => !f.plate.trim())} data-testid="submit-batch-btn">
                {adding ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3" />Ajouter tous</>}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-[11px] text-zinc-400" onClick={() => { setBatchForms([]); setSelectedTemplates(new Set()); }}>Annuler</Button>
            </div>
          </div>
          {batchForms.map((f, i) => (
            <div key={i} className="p-3 border rounded-lg bg-zinc-50/80 space-y-2" data-testid={`batch-form-${i}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: VT_COLORS[f.type] || '#9ca3af' }} />
                  <span className="text-xs font-semibold text-zinc-700">{f.typeLabel}{f.capacity ? ` — ${f.capacity}T` : ''}</span>
                </div>
                <button onClick={() => removeBatchForm(i)} className="text-zinc-300 hover:text-red-500 transition-colors"><X className="h-3.5 w-3.5" /></button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Plaque (ex: CI-1234-AB)" value={f.plate} onChange={e => updateBatchForm(i, 'plate', e.target.value.toUpperCase())}
                  className="h-8 text-sm col-span-2" data-testid={`batch-plate-${i}`} />
                <Input placeholder="Marque" value={f.brand} onChange={e => updateBatchForm(i, 'brand', e.target.value)}
                  className="h-8 text-sm" data-testid={`batch-brand-${i}`} />
                <Input placeholder="Modèle" value={f.model} onChange={e => updateBatchForm(i, 'model', e.target.value)}
                  className="h-8 text-sm" data-testid={`batch-model-${i}`} />
                <Input placeholder="Capacite (T)" type="number" value={f.capacity} onChange={e => updateBatchForm(i, 'capacity', e.target.value)}
                  className="h-8 text-sm bg-emerald-50/50 border-emerald-200 col-span-2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Single add form */}
      {showAdd && (
        <div
          className="p-3 border rounded-lg space-y-2 bg-zinc-50"
          data-testid="add-vehicle-form"
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addVehicle(); } }}
        >
          {fleetItems.length > 0 && (
            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
              <p className="text-[10px] text-zinc-400">Pre-rempli :</p>
              {fleetItems.map((f, i) => (
                <button key={i} onClick={() => prefillFromFleet(f)}
                  className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors ${newType === normalizeVT(f.vehicle_type) ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-zinc-200 text-zinc-500 hover:border-emerald-200'}`}
                  data-testid={`prefill-type-${i}`}>
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: VT_COLORS[normalizeVT(f.vehicle_type)] || '#9ca3af' }} />
                    {vtLabel(f.vehicle_type)}{f.capacity_tons ? ` ${f.capacity_tons}T` : ''}
                  </span>
                </button>
              ))}
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">
              Plaque <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="ex: AH9254-98"
              value={newPlate}
              onChange={e => { setNewPlate(e.target.value.toUpperCase()); setPlateError(''); }}
              className={`h-9 text-sm font-bold tracking-wide ${plateError ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
              style={mono}
              autoFocus
              data-testid="new-vehicle-plate"
            />
            {plateError && <p className="text-[11px] text-red-600 mt-1" data-testid="new-vehicle-plate-error">{plateError}</p>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Type</label>
              <div data-testid="new-vehicle-type">
                <VehicleTypeSelector
                  value={newType}
                  onChange={setNewType}
                  placeholder="Sélectionner un type"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Capacité (T)</label>
              <Input placeholder="ex: 80" type="number" value={newCapacity} onChange={e => setNewCapacity(e.target.value)} className="h-9 text-sm" data-testid="new-vehicle-capacity" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400">Marque</label>
              <Input placeholder="ex: MAN" value={newBrand} onChange={e => setNewBrand(e.target.value)} className="h-9 text-sm" data-testid="new-vehicle-brand" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400">Modèle</label>
              <Input placeholder="ex: TGX 7272" value={newModel} onChange={e => setNewModel(e.target.value)} className="h-9 text-sm" data-testid="new-vehicle-model" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="new-vehicle-has-trailer"
              checked={newHasTrailer}
              onCheckedChange={(c) => { setNewHasTrailer(!!c); if (!c) setNewTrailerPlate(''); }}
              data-testid="new-vehicle-has-trailer"
            />
            <label htmlFor="new-vehicle-has-trailer" className="text-xs text-zinc-600 cursor-pointer">Avec remorque</label>
            {newHasTrailer && (
              <Input
                placeholder="Plaque remorque"
                value={newTrailerPlate}
                onChange={e => setNewTrailerPlate(e.target.value.toUpperCase())}
                className="h-9 text-sm flex-1 font-bold tracking-wide"
                style={mono}
                data-testid="new-vehicle-trailer-plate"
              />
            )}
          </div>
          {quickAdded.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid="quick-added-list">
              <span className="text-[10px] text-emerald-700 font-semibold">{quickAdded.length} ajouté{quickAdded.length > 1 ? 's' : ''} :</span>
              {quickAdded.map(p => (
                <Badge key={p} className="text-[9px] h-4 px-1.5 bg-emerald-50 text-emerald-700 border-emerald-200 rounded-full" style={mono}>{p}</Badge>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 rounded-lg" onClick={addVehicle} disabled={adding} data-testid="confirm-add-vehicle">
              {adding ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Ajouter'}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-zinc-400" onClick={() => { setShowAdd(false); setNewPlate(''); setNewBrand(''); setNewModel(''); setNewCapacity(''); setNewHasTrailer(false); setNewTrailerPlate(''); setPlateError(''); setQuickAdded([]); }}>Fermer</Button>
            <span className="text-[10px] text-zinc-400 ml-auto">Entrée pour enchaîner</span>
          </div>
        </div>
      )}

      {/* Vehicles list or empty state */}
      {vehicles.length === 0 && !showAdd && batchForms.length === 0 && fleetItems.length === 0 ? (
        <div className="text-center py-12 px-4">
          <div className="w-12 h-12 rounded-2xl bg-zinc-100 flex items-center justify-center mx-auto mb-3"><Truck className="h-6 w-6 text-zinc-300" /></div>
          <p className="text-sm font-medium text-zinc-500" style={heading}>Aucun véhicule identifie</p>
          <p className="text-xs text-zinc-400 mt-1">Ajoutez les véhicules de ce partenaire</p>
        </div>
      ) : vehicles.length === 0 && !showAdd && batchForms.length === 0 && fleetItems.length > 0 ? (
        <div className="text-center py-4 px-4">
          <p className="text-xs text-zinc-400">Selectionnez les types ci-dessus puis cliquez "Enrichir"</p>
        </div>
      ) : vehicles.length > 0 && (
        <div className="space-y-1.5">
          {vehicles.map(v => (
            <div key={v.id} className="flex items-center gap-2.5 py-2 px-2.5 rounded-lg hover:bg-zinc-50 border border-zinc-100 transition-colors group" data-testid={`vehicle-${v.id}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-white text-[9px] font-bold ${VT_BG[normalizeVT(v.vehicleType)] || 'bg-gray-400'}`}>
                {vtLabel(v.vehicleType).substring(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-zinc-800" style={mono}>{getPlate(v)}</p>
                  {v.brand && <span className="text-[10px] text-zinc-400">{v.brand}{v.model ? ` ${v.model}` : ''}</span>}
                  {v.genre && <Badge variant="outline" className="text-[9px] h-4 px-1.5 bg-blue-50/50 border-blue-200 text-blue-600">{v.genre}</Badge>}
                  {v.hasTrailer && <Badge variant="outline" className="text-[9px] h-4 px-1.5 bg-violet-50/50 border-violet-200 text-violet-600" data-testid={`vehicle-trailer-${v.id}`}>+ remorque{v.trailerPlate ? ` ${v.trailerPlate}` : ''}</Badge>}
                </div>
                <p className="text-[10px] text-zinc-400">
                  {vtIcon(v.vehicleType)} {vtLabel(v.vehicleType)}
                  {getCapacity(v) ? ` · ${getCapacity(v)}T` : ''}
                  {v.cuKg ? ` · CU: ${v.cuKg}Kg` : ''}
                  {v.ptacKg ? ` · PTAC: ${v.ptacKg}Kg` : ''}
                  {v.color ? ` · ${v.color}` : ''}
                  {v.energy ? ` · ${v.energy}` : ''}
                  {v.axles ? ` · ${v.axles} essieux` : ''}
                </p>
              </div>
              <Badge className={`text-[9px] h-4 px-1.5 border font-medium rounded-full ${v.status === 'disponible' ? AVAIL.disponible.cls : AVAIL.inconnu.cls}`}>{v.status || 'inconnu'}</Badge>
              {(v.addedFrom === 'operation' || v.source === 'operation_auto') && <Badge className="text-[9px] h-4 px-1.5 bg-blue-50 text-blue-600 border-blue-200 rounded-full">via OP</Badge>}
              <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-zinc-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all" onClick={() => removeVehicle(v.id)} data-testid={`remove-vehicle-${v.id}`}>
                <Trash2 className="h-3 w-3 mr-0.5" />Retirer
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// --- Real Drivers Content (CRUD) ---
const RealDriversContent = ({ partnerId, drivers, onRefresh }) => {
  const navigate = useNavigate();
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [adding, setAdding] = useState(false);

  const addDriver = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/api/transport-partners/${partnerId}/drivers`, {
        name: newName.trim(),
        phone: newPhone.trim(),
      }, { headers: { 'Authorization': 'Bearer cookie-auth' } });
      setNewName(''); setNewPhone(''); setShowAdd(false);
      onRefresh();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setAdding(false); }
  };

  const removeDriver = async (driverId) => {
    try {
      await axios.delete(`${API}/api/transport-partners/${partnerId}/drivers/${driverId}`, { headers: { 'Authorization': 'Bearer cookie-auth' } });
      onRefresh();
    } catch (e) {
      toast({ title: 'Erreur de suppression', variant: 'destructive' });
    }
  };

  // Support both embedded (name) and main collection (firstName+lastName)
  const getDriverName = (d) => d.name || `${d.firstName || ''} ${d.lastName || ''}`.trim() || '?';
  const getInitials = (d) => {
    const n = getDriverName(d);
    const parts = n.split(' ');
    return parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : n.substring(0, 2).toUpperCase();
  };

  return (
    <div className="space-y-3" data-testid="real-drivers-content">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">Chauffeurs ({drivers.length})</span>
        <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => setShowAdd(!showAdd)} data-testid="add-driver-btn">
          <Plus className="h-3 w-3 mr-1" />Ajouter
        </Button>
      </div>
      {showAdd && (
        <div className="p-3 border rounded-lg space-y-2 bg-zinc-50">
          <Input placeholder="Nom complet" value={newName} onChange={e => setNewName(e.target.value)} className="h-8 text-sm" data-testid="new-driver-name" />
          <Input placeholder="Telephone" value={newPhone} onChange={e => setNewPhone(e.target.value)} className="h-8 text-sm" data-testid="new-driver-phone" />
          <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={addDriver} disabled={adding} data-testid="confirm-add-driver">
            {adding ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Ajouter'}
          </Button>
        </div>
      )}
      {drivers.length === 0 ? <EmptyBlock icon={Users} text="Aucun chauffeur enregistré" /> : (
        <div className="space-y-1.5">
          {drivers.map(d => (
            <div key={d.id} className="flex items-center gap-2.5 py-2 px-2 rounded-sm hover:bg-zinc-50 border border-zinc-100 cursor-pointer group" data-testid={`driver-${d.id}`}
              onClick={() => navigate(`/admin/drivers/${d.id}/edit`)}>
              <div className="w-7 h-7 rounded-full bg-zinc-200 flex items-center justify-center text-[9px] font-bold text-zinc-500">
                {getInitials(d)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-800 truncate group-hover:text-emerald-700 transition-colors">{getDriverName(d)}{d.isSelf && ' (proprietaire)'}</p>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] text-zinc-400">{d.phone && !d.phone.startsWith('no-phone') ? d.phone : 'Pas de téléphone'}</span>
                  {d.permitCategories && Array.isArray(d.permitCategories) && d.permitCategories.length > 0 && (
                    <div className="flex items-center gap-0.5">
                      {d.permitCategories.map((cat, ci) => {
                        const isExpired = cat.expiresAt && cat.expiresAt !== 'PERMANENT' && cat.expiresAt !== 'permanent' && new Date(cat.expiresAt) < new Date();
                        const isCritical = ['C', 'D', 'E', 'CE', 'EC'].includes(cat.category);
                        return (
                          <span key={ci} className={`text-[8px] font-bold px-1 py-0 rounded ${
                            isExpired ? 'bg-red-100 text-red-600 line-through' :
                            isCritical ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-600'
                          }`} title={`${cat.category}: ${cat.expiresAt || 'N/A'}`}>{cat.category}</span>
                        );
                      })}
                    </div>
                  )}
                  {!d.permitCategories && d.permitType && (
                    <span className="text-[8px] font-bold px-1 py-0 rounded bg-gray-100 text-gray-600">{d.permitType}</span>
                  )}
                </div>
              </div>
              <Badge className={`text-[9px] h-4 px-1.5 border ${d.status === 'disponible' ? AVAIL.disponible.cls : AVAIL.inconnu.cls}`}>{d.status || 'inconnu'}</Badge>
              {(d.addedFrom === 'operation' || d.source === 'operation_auto') && <Badge className="text-[9px] h-4 px-1 bg-blue-50 text-blue-600 border-blue-200">via OP</Badge>}
              <Button variant="ghost" size="icon" className="h-6 w-6 text-zinc-300 hover:text-emerald-600" data-testid={`edit-driver-${d.id}`}
                onClick={(e) => { e.stopPropagation(); navigate(`/admin/drivers/${d.id}/edit`); }}>
                <Pencil className="h-3 w-3" />
              </Button>
              <Button variant="ghost" size="icon" className="h-6 w-6 text-zinc-300 hover:text-red-500" onClick={(e) => { e.stopPropagation(); removeDriver(d.id); }} data-testid={`remove-driver-${d.id}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};



// ============================================================
// Main Component
// ============================================================
const TransportPartners = () => {
  const { hasPermission, isAdmin } = usePermissions();
  const { user } = useAuth();
  // RBAC V2 Phase 6 — transport_partners.view/manage
  const canManage = isAdmin || hasPermission(PERMS.TRANSPORT_PARTNERS_MANAGE);
  // Flotte partagée (juin 2026) : commercial / lead_commercial peuvent créer/éditer
  // mais PAS supprimer un transporteur (aligné sur le backend require_delete_access).
  // Flotte & tiers (juin 2026) : tous les rôles ops & commerciaux peuvent supprimer un transporteur.
  const canDelete = isAdmin || canManage;
  const [partners, setPartners] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [overview, setOverview] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterAvail, setFilterAvail] = useState('');
  const [filterVehicleType, setFilterVehicleType] = useState('');
  const [filterDriverName, setFilterDriverName] = useState('');
  const [filterPlate, setFilterPlate] = useState('');
  const [filterMinCapacity, setFilterMinCapacity] = useState('');
  const [filterMaxCapacity, setFilterMaxCapacity] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [view, setView] = useState('partners');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [dPage, setDPage] = useState(1);
  const [dTotalPages, setDTotalPages] = useState(1);
  const [dTotal, setDTotal] = useState(0);

  // CRUD state
  const [formOpen, setFormOpen] = useState(false);
  const [editPartner, setEditPartner] = useState(null);
  const [deleteIds, setDeleteIds] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showCSVImport, setShowCSVImport] = useState(false);

  const activeFilterCount = [filterVehicleType, filterDriverName, filterPlate, filterMinCapacity, filterMaxCapacity].filter(Boolean).length;

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (search) p.append('search', search);
      if (filterType) p.append('type', filterType);
      if (filterAvail) p.append('availability', filterAvail);
      if (filterVehicleType) p.append('vehicle_type', filterVehicleType);
      if (filterDriverName) p.append('driver_name', filterDriverName);
      if (filterPlate) p.append('vehicle_plate', filterPlate);
      if (filterMinCapacity) p.append('min_capacity', filterMinCapacity);
      if (filterMaxCapacity) p.append('max_capacity', filterMaxCapacity);
      p.append('page', page); p.append('limit', 50);
      const res = await axios.get(`${API}/api/transport-partners?${p}`, { headers: headers() });
      setPartners(res.data.partners || []); setTotal(res.data.total || 0); setTotalPages(res.data.pages || 1);
    } catch { toast({ title: 'Erreur chargement', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [search, filterType, filterAvail, filterVehicleType, filterDriverName, filterPlate, filterMinCapacity, filterMaxCapacity, page]);

  const fetchDrivers = useCallback(async () => {
    setLoadingDrivers(true);
    try {
      const p = new URLSearchParams();
      if (search) p.append('search', search);
      p.append('min_trucks', 1); p.append('page', dPage); p.append('limit', 50);
      const res = await axios.get(`${API}/api/transport-partners/independent-drivers?${p}`, { headers: headers() });
      setDrivers(res.data.drivers || []); setDTotal(res.data.total || 0); setDTotalPages(res.data.pages || 1);
    } catch { toast({ title: 'Erreur chargement', variant: 'destructive' }); }
    finally { setLoadingDrivers(false); }
  }, [search, dPage]);

  const fetchOverview = useCallback(async () => {
    try { const res = await axios.get(`${API}/api/transport-partners/fleet-overview`, { headers: headers() }); setOverview(res.data); } catch {}
  }, []);

  useEffect(() => { fetchPartners(); fetchOverview(); }, [fetchPartners, fetchOverview]);
  useEffect(() => { if (view === 'independents') fetchDrivers(); }, [view, fetchDrivers]);

  const selectPartner = async (p) => {
    try { const res = await axios.get(`${API}/api/transport-partners/${p.id}`, { headers: headers() }); setSelected(res.data); setSelectedType('partner'); } catch {}
  };
  const selectDriver = (d) => { setSelected(d); setSelectedType('driver'); };

  const refresh = () => {
    fetchPartners(); fetchOverview(); setSelectedIds(new Set());
    if (view === 'independents') fetchDrivers();
    // Rafraîchit aussi le panneau de détail ouvert (flotte, véhicules, chauffeurs)
    if (selected?.id && selectedType === 'partner') {
      axios.get(`${API}/api/transport-partners/${selected.id}`, { headers: headers() })
        .then(res => setSelected(res.data))
        .catch(() => {});
    }
  };

  const clearAllFilters = () => {
    setSearch(''); setFilterType(''); setFilterAvail('');
    setFilterVehicleType(''); setFilterDriverName('');
    setFilterPlate(''); setFilterMinCapacity(''); setFilterMaxCapacity('');
    setPage(1); setDPage(1);
  };

  // Selection
  const toggleSelect = (id) => {
    setSelectedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === partners.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(partners.map(p => p.id)));
  };

  // Delete
  const handleDeleteSingle = (id) => { setDeleteIds([id]); setDeleteOpen(true); };
  const handleDeleteSelected = () => { setDeleteIds([...selectedIds]); setDeleteOpen(true); };
  const confirmDelete = async () => {
    try {
      if (deleteIds.length === 1) {
        await axios.delete(`${API}/api/transport-partners/${deleteIds[0]}`, { headers: headers() });
      } else {
        await axios.post(`${API}/api/transport-partners/bulk-delete`, { ids: deleteIds }, { headers: headers() });
      }
      toast({ title: `${deleteIds.length} partenaire(s) supprime(s)` });
      setDeleteOpen(false); setDeleteIds([]);
      if (selected && deleteIds.includes(selected.id)) { setSelected(null); setSelectedType(null); }
      refresh();
    } catch { toast({ title: 'Erreur suppression', variant: 'destructive' }); }
  };

  // Edit
  const handleEdit = (p) => { setEditPartner(p); setFormOpen(true); };
  const handleCreate = () => { setEditPartner(null); setFormOpen(true); };
  const handleFormSaved = () => { refresh(); if (selected && editPartner?.id === selected.id) selectPartner(selected); };

  const eco = overview?.ecosystem || {};
  const isLoading = view === 'partners' ? loading : loadingDrivers;
  const currentPage = view === 'partners' ? page : dPage;
  const currentTotalPages = view === 'partners' ? totalPages : dTotalPages;
  const setCurrentPage = view === 'partners' ? setPage : setDPage;
  const hasSelection = selectedIds.size > 0;

  return (
    <>
      <div className="h-[calc(100vh-64px)] flex flex-col bg-white" data-testid="transport-partners-page">
        {/* STATS TICKER */}
        <div className="flex items-center justify-between px-6 py-5 border-b flex-shrink-0 bg-white">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900 tracking-tight" style={heading}>Gestion des Transporteurs</h1>
            <p className="text-sm text-zinc-500 mt-0.5">Partenaires de transport et chauffeurs independants</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportFormatMenu
              endpoint={`${API}/api/export/partners`}
              filenameBase="BaobabConnect_Transporteurs"
              testId="export-partners"
              size="sm"
              className="h-9 text-sm rounded-md gap-2 shadow-sm"
              axiosConfig={{ headers: { Authorization: 'Bearer cookie-auth' } }}
              onToast={toast}
            />
            <Button size="sm" variant="outline" className="h-9 text-sm rounded-md gap-2 shadow-sm" onClick={() => setShowCSVImport(true)} disabled={!canManage} title={!canManage ? 'Permission requise : transport_partners.manage' : ''} data-testid="import-partners-csv-btn">
              <Upload className="h-4 w-4" />Importer
            </Button>
            <Button className="h-9 text-sm rounded-md bg-emerald-600 hover:bg-emerald-700 gap-2 shadow-sm" onClick={handleCreate} disabled={!canManage} title={!canManage ? 'Permission requise : transport_partners.manage' : ''} data-testid="create-partner-btn">
              <Plus className="h-4 w-4" />Nouveau partenaire
            </Button>
          </div>
        </div>
        
        {/* Overview Stats - Bento Grid */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 px-6 py-4 border-b flex-shrink-0 bg-zinc-50/40">
            {[
              { icon: Truck, label: 'Camions', value: eco.total_trucks || 0, color: 'text-emerald-600', bg: 'bg-emerald-50', iconBg: 'bg-emerald-100' },
              { icon: Building2, label: 'Partenaires', value: eco.partners_count || 0, color: 'text-blue-600', bg: 'bg-blue-50', iconBg: 'bg-blue-100' },
              { icon: UserCheck, label: 'Independants', value: eco.independent_drivers || 0, color: 'text-amber-600', bg: 'bg-amber-50', iconBg: 'bg-amber-100' },
              { icon: Package, label: 'Vehicules', value: eco.registered_vehicles || 0, color: 'text-violet-600', bg: 'bg-violet-50', iconBg: 'bg-violet-100' },
              { icon: Weight, label: 'CU Totale', value: eco.total_cu_kg ? `${Math.round(eco.total_cu_kg / 1000)}T` : '0T', color: 'text-rose-600', bg: 'bg-rose-50', iconBg: 'bg-rose-100' },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-lg border border-zinc-200/80 p-4 shadow-[0_1px_3px_0_rgb(0,0,0,0.04)] hover:shadow-[0_1px_3px_0_rgb(0,0,0,0.1)] transition-shadow" data-testid={`stat-card-${s.label.toLowerCase()}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{s.label}</p>
                    <p className={`text-3xl font-bold ${s.color} mt-1`} style={heading}>{s.value}</p>
                  </div>
                  <div className={`w-10 h-10 rounded-lg ${s.iconBg} flex items-center justify-center`}>
                    <s.icon className={`h-5 w-5 ${s.color}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* TOOLBAR — glassmorphism */}
        <div className="flex items-center gap-3 px-6 py-2.5 border-b flex-shrink-0 backdrop-blur-md bg-white/90 sticky top-0 z-10">
          <div className="flex items-center bg-zinc-100 rounded-lg p-0.5">
            <button onClick={() => { setView('partners'); setSelected(null); setSelectedType(null); setSelectedIds(new Set()); }}
              className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${view === 'partners' ? 'bg-white shadow-sm text-zinc-800' : 'text-zinc-500 hover:text-zinc-700'}`} data-testid="tab-partners">
              Partenaires <span className="text-[11px] ml-1 opacity-50" style={mono}>{total}</span>
            </button>
            <button onClick={() => { setView('independents'); setSelected(null); setSelectedType(null); setSelectedIds(new Set()); }}
              className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${view === 'independents' ? 'bg-white shadow-sm text-zinc-800' : 'text-zinc-500 hover:text-zinc-700'}`} data-testid="tab-independents">
              Independants <span className="text-[11px] ml-1 opacity-50" style={mono}>{dTotal || eco.independent_drivers || '...'}</span>
            </button>
          </div>
          <div className="relative flex-1 max-w-md group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 group-focus-within:text-emerald-500 transition-colors" />
            <Input placeholder="Rechercher nom, tel, chauffeur, plaque..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); setDPage(1); }}
              className="pl-9 pr-8 h-9 text-sm rounded-lg border-zinc-200 focus:border-emerald-300 focus:ring-emerald-200" data-testid="search-input" />
            {search && <button className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors" onClick={() => { setSearch(''); setPage(1); }}><X className="h-3.5 w-3.5" /></button>}
          </div>
          {view === 'partners' && (
            <>
              <Select value={filterType || 'all'} onValueChange={v => { setFilterType(v === 'all' ? '' : v); setPage(1); }}>
                <SelectTrigger className="w-[140px] h-9 text-sm rounded-lg shadow-sm" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tous types</SelectItem>{Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={filterAvail || 'all'} onValueChange={v => { setFilterAvail(v === 'all' ? '' : v); setPage(1); }}>
                <SelectTrigger className="w-[130px] h-9 text-sm rounded-lg shadow-sm" data-testid="filter-avail"><SelectValue placeholder="Statut" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tous</SelectItem>{Object.entries(AVAIL).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button
                variant={showAdvancedFilters ? 'default' : 'outline'}
                size="sm"
                className={`h-9 text-sm rounded-lg gap-1.5 transition-all duration-200 shadow-sm ${showAdvancedFilters ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                data-testid="toggle-advanced-filters"
              >
                <SlidersHorizontal className="h-4 w-4" />
                Filtres
                {activeFilterCount > 0 && (
                  <span className={`ml-0.5 text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center ${showAdvancedFilters ? 'bg-white text-emerald-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </>
          )}
          {hasSelection && view === 'partners' && canDelete && (
            <>
              <div className="h-5 w-px bg-zinc-200" />
              <Badge className="text-xs h-6 bg-emerald-100 text-emerald-700 border-emerald-300 rounded-full px-2.5">{selectedIds.size} selectionne{selectedIds.size > 1 ? 's' : ''}</Badge>
              <Button variant="outline" size="sm" className="h-8 text-xs rounded-lg text-red-600 border-red-200 hover:bg-red-50 gap-1 shadow-sm" onClick={handleDeleteSelected} data-testid="bulk-delete-btn">
                <Trash2 className="h-3.5 w-3.5" />Supprimer
              </Button>
              <Button variant="ghost" size="sm" className="h-6 text-xs text-zinc-400 hover:text-zinc-600" onClick={() => setSelectedIds(new Set())}>Deselectionner</Button>
            </>
          )}
          <Button variant="ghost" size="sm" className="h-8 text-sm text-zinc-400 hover:text-zinc-600 transition-colors" onClick={refresh} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* ADVANCED FILTERS BAR */}
        {showAdvancedFilters && view === 'partners' && (
          <div className="px-6 py-3 border-b bg-zinc-50/60 flex-shrink-0" data-testid="advanced-filters-panel">
            <div className="flex items-center gap-3 flex-wrap">
              <Select value={filterVehicleType || 'all'} onValueChange={v => { setFilterVehicleType(v === 'all' ? '' : v); setPage(1); }}>
                <SelectTrigger className="w-[160px] h-8 text-sm rounded-lg bg-white shadow-sm" data-testid="filter-vehicle-type">
                  <SelectValue placeholder="Type vehicule" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous véhicules</SelectItem>
                  {Object.entries(VT_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: VT_COLORS[k] || '#9ca3af' }} />
                        {v}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="relative group">
                <Users className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400 group-focus-within:text-emerald-500 transition-colors" />
                <Input placeholder="Nom chauffeur" value={filterDriverName}
                  onChange={e => { setFilterDriverName(e.target.value); setPage(1); }}
                  className="pl-8 h-8 w-[160px] text-sm rounded-lg bg-white shadow-sm" data-testid="filter-driver-name" />
                {filterDriverName && <button className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors" onClick={() => { setFilterDriverName(''); setPage(1); }}><X className="h-3 w-3" /></button>}
              </div>

              <div className="relative group">
                <Hash className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400 group-focus-within:text-emerald-500 transition-colors" />
                <Input placeholder="Plaque vehicule" value={filterPlate}
                  onChange={e => { setFilterPlate(e.target.value); setPage(1); }}
                  className="pl-8 h-8 w-[160px] text-sm rounded-lg bg-white shadow-sm" data-testid="filter-vehicle-plate" />
                {filterPlate && <button className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors" onClick={() => { setFilterPlate(''); setPage(1); }}><X className="h-3 w-3" /></button>}
              </div>

              <div className="flex items-center gap-1.5">
                <Weight className="h-3.5 w-3.5 text-zinc-400" />
                <Input type="number" min="0" placeholder="Min T" value={filterMinCapacity}
                  onChange={e => { setFilterMinCapacity(e.target.value); setPage(1); }}
                  className="h-8 w-[80px] text-sm rounded-lg bg-white shadow-sm text-center" data-testid="filter-min-capacity" />
                <span className="text-xs text-zinc-300 font-medium">—</span>
                <Input type="number" min="0" placeholder="Max T" value={filterMaxCapacity}
                  onChange={e => { setFilterMaxCapacity(e.target.value); setPage(1); }}
                  className="h-8 w-[80px] text-sm rounded-lg bg-white shadow-sm text-center" data-testid="filter-max-capacity" />
              </div>

              {(activeFilterCount > 0 || filterType || filterAvail) && (
                <Button variant="ghost" size="sm" className="h-8 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 gap-1 rounded-lg" onClick={clearAllFilters} data-testid="clear-all-filters">
                  <FilterX className="h-3.5 w-3.5" />Effacer tout
                </Button>
              )}
            </div>
            {activeFilterCount > 0 && (
              <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                {filterVehicleType && (
                  <Badge variant="outline" className="text-xs h-6 gap-1.5 bg-white rounded-full px-2.5 shadow-sm">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: VT_COLORS[filterVehicleType] || '#9ca3af' }} />
                    {VT_LABELS[filterVehicleType] || filterVehicleType}
                    <button onClick={() => { setFilterVehicleType(''); setPage(1); }} className="hover:text-red-500 transition-colors"><X className="h-3 w-3" /></button>
                  </Badge>
                )}
                {filterDriverName && (
                  <Badge variant="outline" className="text-xs h-6 gap-1 bg-white rounded-full px-2.5 shadow-sm">
                    <Users className="h-3 w-3 text-zinc-400" />Chauffeur: {filterDriverName}
                    <button onClick={() => { setFilterDriverName(''); setPage(1); }} className="hover:text-red-500 transition-colors"><X className="h-3 w-3" /></button>
                  </Badge>
                )}
                {filterPlate && (
                  <Badge variant="outline" className="text-xs h-6 gap-1 bg-white rounded-full px-2.5 shadow-sm">
                    <Hash className="h-3 w-3 text-zinc-400" />Plaque: {filterPlate}
                    <button onClick={() => { setFilterPlate(''); setPage(1); }} className="hover:text-red-500 transition-colors"><X className="h-3 w-3" /></button>
                  </Badge>
                )}
                {(filterMinCapacity || filterMaxCapacity) && (
                  <Badge variant="outline" className="text-xs h-6 gap-1 bg-white rounded-full px-2.5 shadow-sm">
                    <Weight className="h-3 w-3 text-zinc-400" />Capacite: {filterMinCapacity || '0'}T — {filterMaxCapacity || '...'}T
                    <button onClick={() => { setFilterMinCapacity(''); setFilterMaxCapacity(''); setPage(1); }} className="hover:text-red-500 transition-colors"><X className="h-3 w-3" /></button>
                  </Badge>
                )}
              </div>
            )}
          </div>
        )}

        {/* MAIN CONTENT */}
        <div className="flex-1 overflow-hidden">
          <ResizablePanelGroup direction="horizontal">
            <ResizablePanel defaultSize={selected ? 42 : 100} minSize={30}>
              <div className="h-full flex flex-col">
                <ScrollArea className="flex-1">
                  {view === 'partners' ? (
                    <PartnersTable data={partners} loading={loading} selectedId={selectedType === 'partner' ? selected?.id : null}
                      onSelect={selectPartner} selectedIds={selectedIds} onToggleSelect={toggleSelect} onToggleAll={toggleSelectAll}
                      onEdit={handleEdit} onDelete={handleDeleteSingle} canDelete={canDelete} />
                  ) : (
                    <DriversTable data={drivers} loading={loadingDrivers} selectedId={selectedType === 'driver' ? selected?.id : null} onSelect={selectDriver} />
                  )}
                </ScrollArea>
                {currentTotalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-1.5 border-t bg-zinc-50/50 text-[10px] text-zinc-400">
                    <span style={mono}>{view === 'partners' ? total : dTotal} resultats</span>
                    <div className="flex items-center gap-1.5">
                      <Button variant="outline" size="sm" className="h-5 text-[10px] px-2 rounded-sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prec.</Button>
                      <span style={mono}>{currentPage}/{currentTotalPages}</span>
                      <Button variant="outline" size="sm" className="h-5 text-[10px] px-2 rounded-sm" disabled={currentPage === currentTotalPages} onClick={() => setCurrentPage(p => p + 1)}>Suiv.</Button>
                    </div>
                  </div>
                )}
              </div>
            </ResizablePanel>
            {selected && (
              <>
                <ResizableHandle withHandle />
                <ResizablePanel defaultSize={58} minSize={35}>
                  <div className="shadow-[-4px_0_24px_rgba(0,0,0,0.06)]">
                    <DetailPanel item={selected} type={selectedType} onClose={() => { setSelected(null); setSelectedType(null); }} onRefresh={refresh} onEdit={handleEdit} />
                  </div>
                </ResizablePanel>
              </>
            )}
          </ResizablePanelGroup>
        </div>
      </div>

      {/* Dialogs */}
      <PartnerFormDialog open={formOpen} onClose={() => { setFormOpen(false); setEditPartner(null); }} onSaved={handleFormSaved} editPartner={editPartner} />
      <DeleteDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} onConfirm={confirmDelete} count={deleteIds.length} />
      <CSVImportModal
        open={showCSVImport}
        onOpenChange={setShowCSVImport}
        type="partners"
        columns={[
          { key: 'partnerName', label: 'Transporteur' },
          { key: 'partnerPhone', label: 'Tél.' },
          { key: 'vehicleRegistration', label: 'Immat.' },
          { key: 'vehicleType', label: 'Type Véh.' },
          { key: 'driverName', label: 'Chauffeur' },
        ]}
        onSuccess={() => { fetchPartners(); fetchOverview(); }}
      />
    </>
  );
};

// ============================================================
// Partners Table with checkboxes + actions
// ============================================================
const PartnersTable = ({ data, loading, selectedId, onSelect, selectedIds, onToggleSelect, onToggleAll, onEdit, onDelete, canDelete }) => {
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>;
  if (data.length === 0) return (
    <div className="text-center py-20 px-4">
      <div className="w-14 h-14 rounded-2xl bg-zinc-100 flex items-center justify-center mx-auto mb-4"><Building2 className="h-7 w-7 text-zinc-300" /></div>
      <p className="text-sm font-medium text-zinc-500" style={heading}>Aucun partenaire trouve</p>
      <p className="text-xs text-zinc-400 mt-1">Modifiez vos filtres ou ajoutez un nouveau partenaire</p>
    </div>
  );
  const allSelected = selectedIds.size === data.length && data.length > 0;

  return (
    <Table>
      <TableHeader className="sticky top-0 z-[5]">
        <TableRow className="bg-zinc-50/90 backdrop-blur-sm hover:bg-zinc-50/90 border-b border-zinc-200">
          <TableHead className="w-[40px] h-10 pl-4"><Checkbox checked={allSelected} onCheckedChange={onToggleAll} data-testid="select-all-checkbox" /></TableHead>
          <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10">Partenaire</TableHead>
          <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10">Type</TableHead>
          <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10">Flotte</TableHead>
          <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10">Chauffeurs</TableHead>
          <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10">Vehic.</TableHead>
          <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10">Statut</TableHead>
          <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10">Zone</TableHead>
          <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10 pr-4 w-[80px]">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map(p => {
          const avail = AVAIL[p.availability] || AVAIL.inconnu;
          const isActive = selectedId === p.id;
          const isChecked = selectedIds.has(p.id);
          return (
            <TableRow key={p.id} data-testid={`partner-row-${p.id}`}
              className={`cursor-pointer transition-colors duration-150 h-12 ${isActive ? 'bg-emerald-50/60 border-l-2 border-l-emerald-500' : isChecked ? 'bg-blue-50/30' : 'hover:bg-zinc-50/80'}`}>
              <TableCell className="pl-4 py-2" onClick={e => e.stopPropagation()}>
                <Checkbox checked={isChecked} onCheckedChange={() => onToggleSelect(p.id)} data-testid={`checkbox-${p.id}`} />
              </TableCell>
              <TableCell className="py-2" onClick={() => onSelect(p)}>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-700 shrink-0">{(p.name || '?').substring(0, 2).toUpperCase()}</div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-800 truncate leading-tight">{p.name}</p>
                    {p.phone && <p className="text-[11px] text-zinc-400 truncate" style={mono}>{p.phone}</p>}
                  </div>
                </div>
              </TableCell>
              <TableCell className="py-2" onClick={() => onSelect(p)}><span className="text-xs text-zinc-500">{TYPE_LABELS[p.type] || p.type}</span></TableCell>
              <TableCell className="py-2" onClick={() => onSelect(p)}><FleetCell fleet={p.fleet} totalTrucks={p.totalDeclaredTrucks} /></TableCell>
              <TableCell className="py-2" onClick={() => onSelect(p)}>
                <div className="flex items-center gap-1">
                  <Users className="h-3 w-3 text-zinc-300" />
                  <span className="text-xs font-medium text-zinc-700" style={mono}>{(p.linkedDriverIds || []).length + (p.drivers || []).length}</span>
                </div>
              </TableCell>
              <TableCell className="py-2" onClick={() => onSelect(p)}>
                <div className="flex items-center gap-1">
                  <Truck className="h-3 w-3 text-zinc-300" />
                  <span className="text-xs font-medium text-zinc-700" style={mono}>{(p.linkedVehicleIds || []).length}</span>
                </div>
              </TableCell>
              <TableCell className="py-2" onClick={() => onSelect(p)}><Badge className={`text-[10px] h-5 px-2.5 border font-medium rounded-full ${avail.cls}`}>{avail.label}</Badge></TableCell>
              <TableCell className="py-2" onClick={() => onSelect(p)}>
                <span className="text-xs text-zinc-400 truncate block max-w-[100px]">{(p.coverageZones || []).slice(0, 1).join(', ') || p.intraInterLand || '-'}</span>
              </TableCell>
              <TableCell className="py-2 pr-4" onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-0.5">
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-300 hover:text-blue-600 transition-colors" onClick={() => onEdit(p)} data-testid={`edit-row-${p.id}`}><Edit className="h-3.5 w-3.5" /></Button>
                  {canDelete && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-300 hover:text-red-500 transition-colors" onClick={() => onDelete(p.id)} data-testid={`delete-row-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

// ============================================================
// Drivers Table
// ============================================================
const DriversTable = ({ data, loading, selectedId, onSelect }) => {
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amber-400" /></div>;
  if (data.length === 0) return (
    <div className="text-center py-20 px-4">
      <div className="w-14 h-14 rounded-2xl bg-zinc-100 flex items-center justify-center mx-auto mb-4"><UserCheck className="h-7 w-7 text-zinc-300" /></div>
      <p className="text-sm font-medium text-zinc-500" style={heading}>Aucun chauffeur trouve</p>
      <p className="text-xs text-zinc-400 mt-1">Modifiez vos criteres de recherche</p>
    </div>
  );

  return (
    <Table>
      <TableHeader className="sticky top-0 z-[5]">
        <TableRow className="bg-zinc-50/90 backdrop-blur-sm hover:bg-zinc-50/90 border-b border-zinc-200">
          <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10 pl-4">Chauffeur</TableHead>
          <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10">Camions</TableHead>
          <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10">Flotte</TableHead>
          <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10">Statut</TableHead>
          <TableHead className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider h-10 pr-4">Contact</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map(d => {
          const isActive = selectedId === d.id;
          const statusCls = d.status === 'disponible' ? AVAIL.disponible.cls : AVAIL.inconnu.cls;
          return (
            <TableRow key={d.id} data-testid={`driver-row-${d.id}`} onClick={() => onSelect(d)}
              className={`cursor-pointer transition-colors duration-150 h-11 ${isActive ? 'bg-amber-50/60 border-l-2 border-l-amber-500' : 'hover:bg-zinc-50/80'}`}>
              <TableCell className="pl-4 py-1.5">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center text-[9px] font-bold text-amber-700 shrink-0">{(d.firstName || '?')[0]}{(d.lastName || '?')[0]}</div>
                  <p className="text-[13px] font-medium text-zinc-800 truncate leading-tight" style={heading}>{d.firstName} {d.lastName}</p>
                </div>
              </TableCell>
              <TableCell className="py-1.5"><span className="text-xs font-bold text-zinc-700" style={mono}>{d.totalTrucks}</span></TableCell>
              <TableCell className="py-1.5"><FleetCell fleet={d.fleet} totalTrucks={d.totalTrucks} /></TableCell>
              <TableCell className="py-1.5"><Badge className={`text-[10px] h-[18px] px-2 border font-medium rounded-full ${statusCls}`}>{d.status || 'inconnu'}</Badge></TableCell>
              <TableCell className="py-1.5 pr-4"><span className="text-[11px] text-zinc-400 truncate block max-w-[120px]" style={mono}>{d.phone || '-'}</span></TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default TransportPartners;
